import React, { useEffect, useRef } from 'react';
// Import CSS as string using Vite's ?inline
import tailwindCss from '@/index.css?inline';

interface StyleInjectorProps {
  children: React.ReactNode;
}

/**
 * Injects compiled Tailwind CSS into Shadow DOM
 */
export function StyleInjector({ children }: StyleInjectorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const injectedRef = useRef(false);
  const [stylesReady, setStylesReady] = React.useState(false);

  useEffect(() => {
    if (!containerRef.current || injectedRef.current) return;

    const shadowRoot = containerRef.current.getRootNode() as ShadowRoot;
    if (shadowRoot.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return;

    // Only inject once
    if (shadowRoot.querySelector('style[data-tailwind]')) {
      setStylesReady(true);
      return;
    }

    const style = document.createElement('style');
    style.setAttribute('data-tailwind', 'true');
    style.textContent = tailwindCss;
    shadowRoot.insertBefore(style, shadowRoot.firstChild);
    
    // Add markdown/prose styles
    const proseStyles = document.createElement('style');
    proseStyles.setAttribute('data-prose', 'true');
    proseStyles.textContent = `
      .prose {
        color: inherit;
        max-width: none;
      }
      .prose p {
        margin: 0;
        line-height: 1.6;
      }
      .prose h1, .prose h2, .prose h3, .prose h4 {
        font-weight: 600;
        margin-top: 0.5em;
        margin-bottom: 0.25em;
        line-height: 1.3;
      }
      .prose h1 { font-size: 1.25em; }
      .prose h2 { font-size: 1.125em; }
      .prose h3 { font-size: 1em; }
      .prose ul, .prose ol {
        margin: 0.25em 0;
        padding-left: 1.5em;
      }
      .prose li {
        margin: 0.125em 0;
      }
      .prose strong {
        font-weight: 600;
      }
      .prose em {
        font-style: italic;
      }
      .prose code {
        background-color: rgba(0, 0, 0, 0.05);
        padding: 0.125em 0.25em;
        border-radius: 0.25em;
        font-size: 0.875em;
        font-family: monospace;
      }
      .prose a {
        color: #2563eb;
        text-decoration: underline;
      }
      .prose a:hover {
        opacity: 0.8;
      }
      .prose blockquote {
        border-left: 3px solid #e5e7eb;
        padding-left: 1em;
        font-style: italic;
        margin: 0.5em 0;
      }
      .prose pre {
        background-color: rgba(0, 0, 0, 0.05);
        padding: 0.75em;
        border-radius: 0.5em;
        overflow-x: auto;
        margin: 0.5em 0;
      }
      .prose pre code {
        background-color: transparent;
        padding: 0;
      }
    `;
    shadowRoot.insertBefore(proseStyles, shadowRoot.firstChild);
    
    injectedRef.current = true;
    
    // Use requestAnimationFrame to ensure styles are applied before showing content
    requestAnimationFrame(() => {
      setStylesReady(true);
    });
  }, []);

  return (
    <div ref={containerRef} style={{ opacity: stylesReady ? 1 : 0 }}>
      {children}
    </div>
  );
}
